<template>
    <transition
        v-if="isReady"
        name="fade"
        :duration="333"
        @beforeEnter="onBeforeEnter"
        @enter="onEnter"
        @afterEnter="onAfterEnter"
        @beforeLeave="onBeforeLeave"
        @leave="onLeave"
        @afterLeave="onAfterLeave"
    >
        <div
            ref="drawer"
            class="drawer"
            :class="{ show: is_show }"
            v-bind="dialogAttributes"
            @keydown="onEsc"
            @click.stop
        >
            <div class="drawer__background" @click="onClickOut" />

            <div ref="content" class="drawer__slider">
                <header :id="getId('drawer_header')" class="drawer__header">
                    <div class="drawer__header-primary">
                        <h3 class="drawer__title">
                            {{ title }}
                        </h3>

                        <slot name="header-primary" />

                        <f-button-close
                            :disabled="is_transitioning"
                            :aria-label="headerCloseLabel"
                            :text-variant="headerTextVariant"
                            @click="e => hide('header-close')"
                        >
                            <slot name="drawer-header-close">
                                <font-awesome-icon
                                    class="drawer__close-icon"
                                    :icon="['fal', 'times']"
                                    size="2x"
                                />
                            </slot>
                        </f-button-close>
                    </div>

                    <div v-if="hasSlotContent('header-secondary')" class="drawer__header-secondary">
                        <slot name="header-secondary" />
                    </div>
                </header>

                <div class="drawer__content">
                    <slot />
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapMutations } from 'vuex';

import idMixin from '@/mixins/id';
import fButtonClose from '@/components/shared/modal/modal-button-close';
import keyCodes from '@/utils/key-codes';
import { addClass, removeClass } from '@/utils/dom';
import { HIDE_DRAWER } from '@/store/types/mutation-types';

export default {
    name: 'drawer',
    components: { fButtonClose },
    mixins: [idMixin],

    model: {
        prop: 'visible',
        event: 'change',
    },

    props: {
        headerCloseLabel: {
            type: String,
            default: 'Close',
        },

        headerTextVariant: {
            type: String,
            default: null,
        },

        title: {
            type: String,
            default: 'Content',
        },
    },

    data() {
        return {
            is_transitioning: false,
            is_show: false,
            is_block: false,
            scrollbarWidth: 0,
            isBodyOverflowing: false,
            isReady: false,
        };
    },

    computed: {
        dialogAttributes() {
            return {
                id: this.getId(),
                role: 'dialog',
                'aria-labelledby': this.getId('drawer_header'),
            };
        },
    },

    watch: {
        visible(newVal, oldVal) {
            if (newVal === oldVal) {
                return;
            }

            this[newVal ? 'show' : 'hide']();
        },
    },

    created() {
        // create non-reactive property
        this.observer = null;
    },

    mounted() {
        this.show();
    },

    beforeDestroy() {
        // Ensure everything is back to normal
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        // Re-adjust body/navbar/fixed padding/margins (if needed)
        removeClass(document.body, 'drawer-open');
    },

    methods: {
        ...mapMutations({
            hideDrawer: HIDE_DRAWER,
        }),

        async show() {
            this.$emit('shown');
            this.$root.$emit('flex::drawer::shown');

            await this.$nextTick();

            // We do this after nextTick to ensure the drawer is in DOM first before we show it
            this.isReady = true;
            this.$emit('change', true);
        },

        hide(trigger) {
            if (trigger === 'ok') this.$emit('ok');
            if (trigger === 'cancel') this.$emit('cancel');

            this.$emit('hidden');
            this.$root.$emit('flex::drawer::hidden');

            // stop observing for content changes
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }

            this.$emit('change', false);
            this.hideDrawer();
        },

        // Transition Handlers
        onBeforeEnter() {
            this.is_transitioning = true;
            addClass(document.body, 'drawer-open');
        },

        onEnter() {
            this.is_block = true;
            this.$refs.drawer.scrollTop = 0;
        },

        onAfterEnter() {
            this.is_show = true;
            this.is_transitioning = false;

            this.$nextTick(() => {
                this.focusFirst();
                this.$emit('shown');
                this.$root.$emit('flex::drawer::shown');
            });
        },

        onBeforeLeave() {
            this.is_transitioning = true;
        },

        onLeave() {
            // Remove the 'show' class
            this.is_show = false;
        },

        async onAfterLeave() {
            this.is_block = false;
            this.is_transitioning = false;
            removeClass(document.body, 'drawer-open');

            await this.$nextTick();

            this.$emit('hidden');
            this.$root.$emit('flex::drawer::hidden');
        },

        // UI Event Handlers
        onClickOut() {
            // If backdrop clicked, hide drawer
            if (!this.noCloseOnBackdrop) {
                this.hide('backdrop');
            }
        },

        onEsc(evt) {
            // If ESC pressed, hide drawer
            if (
                evt.keyCode === keyCodes.ESC
                && !this.noCloseOnEsc
            ) {
                this.hide('esc');
            }
        },

        // Focus control handlers
        focusFirst() {
            const content = this.$refs.content;
            const drawer = this.$refs.drawer;
            const activeElement = document.activeElement;

            if (activeElement && content && content.contains(activeElement)) {
                // If activeElement is child of content, no need to change focus
            } else if (content) {
                if (drawer) {
                    drawer.scrollTop = 0;
                }
                // Focus the drawer content wrapper
                content.focus();
            }
        },

        hasSlotContent(slotName) {
            if (!this.$slots[slotName]) return false;

            return this.$slots[slotName].every(slot => slot.context);
        },
    },
};
</script>

<style lang="scss">
.drawer {
    $this: &;
    @extend %fill;
    position: fixed;
    z-index: $zindex-drawer;

    &__background {
        @extend %fill;
        background-color: rgba(0, 0, 0, 0.33);
        transition: opacity 0.33s;

        .fade-enter &,
        .fade-leave-to & {
            opacity: 0;
        }
    }

    &__slider {
        @extend %fill;
        left: auto;
        color: var(--modal-color);
        background-color: var(--modal-backgroundColor);
        transform: translateX(0);
        transition: transform 0.33s;
        width: 93%;
        box-shadow: -2px 0 7px -5px #fff;
        display: flex;
        flex-direction: column;

        .fade-enter &,
        .fade-leave-to & {
            transform: translateX(100%);
        }
    }

    &__header {
        color: var(--modal-headerColor);
        background-image: linear-gradient(var(--modal-headerBackgroundColorTop), var(--modal-headerBackgroundColorBottom));
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
    }

    &__header-primary {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 15px 25px;
    }

    &__header-secondary {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        width: 100%;
    }

    &__title {
        font-size: 22px;
        font-weight: 400;
    }

    &__close {
        border: none;
        padding: 0;
        background-color: transparent;
        font-size: 40px;
    }

    &__close-icon {
        color: var(--modal-headerColor);
    }

    &__content {
        position: relative;
        padding: 0;
        flex: 1;
        max-height: 100%;
        overflow: auto;

        #{ $this }--events & {
            max-height: inherit;
            overflow: visible;
        }
    }

    &.show ~ .rich-text-modal {
        display: none;
    }
}

.flex-page-data {
    margin: 25px 35px 75px;

    &__form {
        max-width: 50%;
        margin-bottom: 40px;
    }

    &__buttons {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;

        display: flex;
        padding: 0;
        margin: 0;
        z-index: 10;
    }

    &__button {
        flex-grow: 1;
        z-index: 2; // issue with z-index for toggles going over button
        min-height: 50px;

        &--continue {
            @extend %button;
        }

        &--cancel {
            @extend %button-alt;
        }
    }
}
</style>
